import { supabase } from './cliente'
import { SubidorSupabase } from '../storage/subidor-supabase'
import { backendDeUrl } from '../storage/rutas-storage'

/**
 * Eliminar proveedor/producto/tela desde el dashboard (pedido de Angie,
 * 2026-09-16) — mismo orden en cascada que ya usa mobile (tela → producto →
 * proveedor, ver casos-de-uso.ts) pero escribiendo directo a Supabase, sin
 * pasar por la cola offline de Dexie (el dashboard siempre tiene internet).
 *
 * Mismo criterio de storage que el resto de la app: solo se borra
 * físicamente lo que vive en Supabase Storage (fotos legado) — lo que vive
 * en S3 nunca se borra de verdad (el rol de la Lambda no tiene
 * `s3:DeleteObject` y el CORS del bucket no permite DELETE desde el
 * navegador, ver sincronizador.ts) — queda como huérfano aceptado, "la
 * ausencia de la fila ES el estado".
 */

const subidorSupabase = new SubidorSupabase()

async function borrarStorageSiAplica(urls: (string | null | undefined)[]): Promise<void> {
  const rutasSupabase = urls
    .map((u) => (u ? backendDeUrl(u) : undefined))
    .filter((r): r is { ruta: string; backend: 'supabase' } => r?.backend === 'supabase')
    .map((r) => r.ruta)
  if (rutasSupabase.length === 0) return
  try {
    await subidorSupabase.borrar(rutasSupabase)
  } catch (err) {
    // Best-effort: preferimos que la fila igual se borre de Postgres a que
    // un fallo de Storage bloquee todo el borrado.
    console.warn('[eliminar-desde-dashboard] no se pudo borrar físicamente de Supabase Storage:', err)
  }
}

export async function eliminarTelaDashboard(telaId: string): Promise<void> {
  const { data, error: errorLectura } = await supabase
    .from('telas')
    .select('foto_frente_url, foto_reverso_url')
    .eq('id', telaId)
    .single()
  if (errorLectura) throw errorLectura

  await borrarStorageSiAplica([data?.foto_frente_url, data?.foto_reverso_url])

  const { error } = await supabase.from('telas').delete().eq('id', telaId)
  if (error) throw error
}

export async function eliminarProductoDashboard(productoId: string): Promise<void> {
  const { data: telas, error: errorTelas } = await supabase
    .from('telas')
    .select('foto_frente_url, foto_reverso_url')
    .eq('producto_id', productoId)
  if (errorTelas) throw errorTelas

  const { data: producto, error: errorProducto } = await supabase
    .from('productos')
    .select('color_estructura_foto_url, fotos')
    .eq('id', productoId)
    .single()
  if (errorProducto) throw errorProducto

  const urls: (string | null | undefined)[] = [producto?.color_estructura_foto_url]
  for (const f of (producto?.fotos ?? []) as { url: string }[]) urls.push(f.url)
  for (const t of telas ?? []) {
    urls.push(t.foto_frente_url)
    urls.push(t.foto_reverso_url)
  }
  await borrarStorageSiAplica(urls)

  // Telas primero (hijas), producto después (padre) — nunca al revés.
  const { error: errorBorrarTelas } = await supabase.from('telas').delete().eq('producto_id', productoId)
  if (errorBorrarTelas) throw errorBorrarTelas

  const { error: errorBorrarProducto } = await supabase.from('productos').delete().eq('id', productoId)
  if (errorBorrarProducto) throw errorBorrarProducto
}

export async function eliminarProveedorDashboard(codigo: string): Promise<void> {
  const { data: productos, error: errorProductos } = await supabase.from('productos').select('id').eq('proveedor_codigo', codigo)
  if (errorProductos) throw errorProductos

  // Cada producto se borra con su propia cascada de telas + storage.
  for (const p of productos ?? []) {
    await eliminarProductoDashboard(p.id)
  }

  const { data: proveedor, error: errorProveedor } = await supabase
    .from('proveedores')
    .select('foto_tarjeta_url')
    .eq('codigo', codigo)
    .single()
  if (errorProveedor) throw errorProveedor
  await borrarStorageSiAplica([proveedor?.foto_tarjeta_url])

  const { error } = await supabase.from('proveedores').delete().eq('codigo', codigo)
  if (error) throw error
}
